import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, shareReplay } from 'rxjs';
import { BreadcrumbGroups } from '../constants';
import {
  MasterPanelState,
  MasterPanelStates,
  WizardResultMode,
  WizardResultModes,
  WizardResultsGroupType
} from '../types';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WizardResultsUIService {

  readonly #currentGroup = new BehaviorSubject<WizardResultsGroupType>('Reports');
  readonly #groupFetching = new Map<WizardResultsGroupType, BehaviorSubject<boolean>>();
  readonly #groupFetching$ = new Map<WizardResultsGroupType, Observable<boolean>>();
  readonly #currentTab = new BehaviorSubject(0);
  readonly #currentMode = new BehaviorSubject<WizardResultMode>(WizardResultModes.Default);
  readonly #masterPanelState = new BehaviorSubject<MasterPanelState>('Open');
  readonly #fetching = new BehaviorSubject(true);

  readonly currentTab$ = this.#currentTab.asObservable();
  readonly currentGroup$ = this.#currentGroup.asObservable();
  readonly currentMode$ = this.#currentMode.asObservable();
  readonly masterPanelState$ = this.#masterPanelState.asObservable();
  readonly fetching$ = this.#fetching.asObservable();

  constructor() {
    BreadcrumbGroups.forEach(key => {
      this.#groupFetching.set(key, new BehaviorSubject(true));
      this.#groupFetching$.set(key, this.#groupFetching.get(key).asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false })));
    });
  }

  get fetching() { return this.#fetching.getValue(); }
  set fetching(fetching: boolean) { this.#fetching.next(fetching); }

  get masterPanelState() { return this.#masterPanelState.getValue(); }
  set masterPanelState(state: MasterPanelState) { this.#masterPanelState.next(state); }

  get mode() { return this.#currentMode.getValue(); }
  set mode(mode: WizardResultMode) { if (this.mode !== mode) { this.#currentMode.next(mode); } }

  get currentTab() { return this.#currentTab.getValue(); }
  get currentGroup() { return this.#currentGroup.getValue(); }

  setGroupFetching(group: WizardResultsGroupType, value: boolean) {
    this.#groupFetching.get(group).next(value);
  }

  getGroupFetching(group: WizardResultsGroupType): Observable<boolean> {
    return this.#groupFetching$.get(group);
  }

  isGroupFetching(group: WizardResultsGroupType): boolean {
    return this.#groupFetching.get(group).value;
  }

  selectGroup(group: WizardResultsGroupType) {
    this.#currentGroup.next(group);
  }

  setCurrentTab(index: number) {
    this.#currentTab.next(index);
  }

  toggleMasterPanel() {
    const state = this.#masterPanelState.getValue();
    this.#masterPanelState.next(state === MasterPanelStates.Close ? MasterPanelStates.Open : MasterPanelStates.Close);
  }

  openMasterPanel() {
    if (this.#masterPanelState.getValue() === MasterPanelStates.Close) {
      this.#masterPanelState.next(MasterPanelStates.Open);
    }
  }

  closeMasterPanel() {
    if (this.#masterPanelState.getValue() === MasterPanelStates.Open) {
      this.#masterPanelState.next(MasterPanelStates.Close);
    }
  }

  resetAllGroupStatus(): WizardResultsUIService {
    BreadcrumbGroups.forEach(key => {
      this.#groupFetching.get(key).next(true);
    });
    return this;
  }

  resetUI(): WizardResultsUIService {
    this.#currentTab.next(0);
    this.#currentMode.next(WizardResultModes.Default);
    return this.resetAllGroupStatus();
  }
}
